import React, {
  useEffect, useRef, useCallback, Fragment, ReactElement, RefObject,
} from 'react';
import TextBold from '../text-bold/text-bold.component';
import TextBlock from '../text-block/text-block.component';
import LineBreak from '../line-break/line-break.component';
import ChartService from '../../services/chart-service';
import FormatService from '../../services/format-service';
import useScreenSmall from '../../hooks/use-screen-small';
import useStylesChartGantt from './chart-gantt.styles';
import { GANTT_CHART_COLOR_LIST } from '../../constants/system/colors';
import './chart-gantt.styles.scss';

const ChartGantt: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const CONTAINER_MIN_HEIGHT: number = 300;

  const {
    isActive,
    isEditMode,
    withAddButton,
    containerId,
    chartZoom,
    chartData,
    chartInitDeps,
    tableVisibleColumns,
    onDataChange,
  } = props;

  const { classes } = useStylesChartGantt();
  const isSmallScreen: boolean = useScreenSmall();
  const containerRef: RefObject<HTMLDivElement> | null = useRef(null);

  const containerChartViewClassName: string | undefined = !isEditMode ? classes.containerChartView : undefined;
  const chartDataTasksTotal: number = chartData && chartData.data ? chartData.data.length : 0;
  const chartDataLinksTotal: number = chartData && chartData.links ? chartData.links.length : 0;

  const infoModalRenderer: Function = useCallback((
    dateStart: string, dateEnd: string, taskAssignedTo: string | null, taskParentName: string | null,
    taskNotes: string | null,
  ): string | null => (
    FormatService.formatComponentToHtml(
      <Fragment>
        <TextBold>
          Start date:
        </TextBold>
        {' '}
        {dateStart}
        <LineBreak />
        <TextBold>
          End date:
        </TextBold>
        {' '}
        {dateEnd}
        <LineBreak />
        {
          !!taskAssignedTo && (
            <Fragment>
              <TextBold>
                Assigned To:
              </TextBold>
              {' '}
              {taskAssignedTo}
              <LineBreak />
            </Fragment>
          )
        }
        {
          !!taskParentName && (
            <Fragment>
              <TextBold>
                Parent Task:
              </TextBold>
              {' '}
              {taskParentName}
              <LineBreak />
            </Fragment>
          )
        }
        {
          !!taskNotes && (
            <Fragment>
              <TextBold>
                Notes:
              </TextBold>
              {' '}
              {taskNotes}
              <LineBreak />
            </Fragment>
          )
        }
      </Fragment>,
    )
  ), []);

  const taskTextRenderer: Function = useCallback((taskName: string): string | null => (
    FormatService.formatComponentToHtml(
      <TextBlock title={taskName}>
        {taskName}
      </TextBlock>,
    )
  ), []);

  useEffect((): ReactUseEffectCleanUp => {
    if (isActive && chartData && chartZoom && containerRef.current) {
      ChartService.createGanttChart(
        containerId,
        containerRef.current,
        chartData,
        chartZoom,
        !isEditMode,
        isSmallScreen,
        onDataChange,
        GANTT_CHART_COLOR_LIST,
        tableVisibleColumns,
        withAddButton,
        infoModalRenderer,
        taskTextRenderer,
      );
    }

    return (): void => {
      ChartService.detachAllGanttChartEventListeners();
    };
  }, [
    isEditMode,
    isSmallScreen,
    chartData,
    chartDataTasksTotal,
    chartDataLinksTotal,
    chartZoom,
    tableVisibleColumns,
    ...chartInitDeps,
  ]);

  return (
    <div
      ref={containerRef}
      id={containerId}
      className={FormatService.formatClassNames(classes.containerChart, containerChartViewClassName)}
      style={{
        minHeight: CONTAINER_MIN_HEIGHT,
      }}
    />
  );
});

export default ChartGantt;
